import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BosDurum, Buton, cn, Ikon, Iskelet, Kart } from "@koclukpanel/ui";
import { ApiHatasi, anketOzetim, isListesiGetir, type IsKalemi } from "./api";
import { KocOzeti } from "./KocOzeti";

interface IsListesiProps {
  /**
   * Satira dokununca cagriliyor. `tur` de veriliyor cunku sartname
   * "her satirdan DOGRUDAN isleme gecilir" diyor: veli raporu bekleyen
   * bir satir program ekranini acsaydi kullanici oradan raporlara
   * ayrica gitmek zorunda kalirdi — yani "ogrenci secme ekranina
   * donulmez" kurali metnen saglanip ruhen ihlal edilirdi.
   */
  onOgrenciSec: (ogrenciId: string, ogrenciAdi: string, tur: IsKalemi["tur"]) => void;
  /**
   * Cozulmus izinler (`/auth/ben`den). Sorgu ATILMADAN once bakiliyor:
   * yetkisi olmayana istek gondermek, her acilista bir "erisim
   * reddedildi" denetim kaydi uretiyordu.
   */
  izinler: Record<string, string>;
  /**
   * `/auth/ben` -> `uzmanMi`. `Coach` KAYDI var mi; rol adi degil.
   *
   * Izinden AYRI bir soru: Operasyon Admini `analiz.ogrenci.goruntule`
   * tasiyor ama uzman degil, uc ona `UZMAN_DEGIL` donuyor.
   */
  uzmanMi: boolean;
}

/**
 * UZMAN ANA EKRANI = IS LISTESI (F-ANL-09)
 *
 * "Bilgi panosu degil." Buyuk sayilar, grafikler ve "hos geldiniz"
 * kartlari YOK. Uzman sabah paneli acinca ne yapacagini goruyor.
 *
 * ================================================================
 * HER SATIRDAN DOGRUDAN ISLEME GECILIR
 * ================================================================
 * Sartnamenin cumlesi: "Her satirdan dogrudan isleme gecilir —
 * ogrenci secme ekranina donulmez." Satira tiklamak ogrenciyi
 * SECMIYOR, o isin ekranini aciyor.
 */
/**
 * Onceligin UC gorsel tasiyicisi.
 *
 * ================================================================
 * SERIT VE KENAR CIZIMDEN — CIP ZATEN VARDI
 * ================================================================
 * `BugunUzman.dc.html` is satirini uc katmanla ciziyor: 3px'lik dikey
 * SOL SERIT (oncelik renginde, `radius-rozet`, satir boyunca uzuyor),
 * onceligin tonunda KENARLIK, ve sagda metin tasiyan CIP.
 *
 * Kodda yalnizca cip vardi. Serit gozun listeyi TARAMASI icin: on
 * satirlik bir listede her satirin cipini okumak yerine sol kenardaki
 * renk dizisi onceligi bir bakista veriyor.
 *
 * Renk yine TEK BASINA anlam tasimiyor — cipin metni yerinde duruyor
 * (03-kod-standartlari erisilebilirlik kurali). Serit onu tekrarliyor,
 * yerine gecmiyor.
 *
 * Cizim yalnizca KRITIK satiri gosteriyor (kenar `hata-200`, serit
 * `hata-500`); otekiler ayni kaliptan, her onceligin kendi ailesiyle.
 */
const ONCELIK_GORUNUM: Record<
  IsKalemi["oncelik"],
  { etiket: string; sinif: string; kenar: string; serit: string }
> = {
  KRITIK: {
    etiket: "Kritik",
    sinif: "bg-eksik-zemin text-eksik-metin ring-eksik-nokta/30",
    kenar: "border-mor-kenar",
    serit: "bg-eksik-nokta",
  },
  YUKSEK: {
    etiket: "Yüksek",
    sinif: "bg-bekliyor-zemin text-bekliyor-metin ring-bekliyor-nokta/40",
    kenar: "border-mor-kenar",
    serit: "bg-bekliyor-nokta",
  },
  ORTA: {
    etiket: "Orta",
    sinif: "bg-planli-zemin text-planli-metin ring-planli-nokta/30",
    kenar: "border-mor-kenar",
    serit: "bg-planli-nokta",
  },
  BILGI: {
    etiket: "Bilgi",
    sinif: "bg-notr-100 text-notr-600 ring-notr-300",
    kenar: "border-mor-kenar",
    serit: "bg-notr-300",
  },
};

/**
 * ================================================================
 * TÜRE GÖRE SÜZGEÇ — 42 SATIRDA AYNI CÜMLE ALTI KEZ (07.09.2026)
 * ================================================================
 * Liste önceliğe göre sıralı ve bu doğru varsayılan. Ama ölçüldü:
 * bir uzmanda 42 iş vardı ve aynı cümle art arda altı kez
 * tekrarlıyordu ("4 gündür veri girmedi"). Aynı türden işleri toplu
 * yapmak isteyen kişi listeyi gözle tarıyordu.
 *
 * Süzgeç SIRALAMAYI DEĞİŞTİRMİYOR: öncelik sırası korunuyor, yalnızca
 * bakılan tür daraltılıyor. Sıralamayı da değiştirseydi "kritik olan
 * neydi" bilgisi kaybolurdu.
 */
const TUR_ETIKETI: Record<IsKalemi["tur"], string> = {
  VERI_GIRMEYEN: "Veri girmeyen",
  PROGRAM_HAZIRLANMAMIS: "Programı yok",
  GORUSME_RAPORU_YAZILMAMIS: "Görüşme raporu",
  VELI_RAPORU_BEKLIYOR: "Veli raporu",
  NET_DUSEN: "Neti düşen",
};

const TUR_EYLEMI: Record<IsKalemi["tur"], string> = {
  VERI_GIRMEYEN: "Programa bak",
  PROGRAM_HAZIRLANMAMIS: "Program hazırla",
  GORUSME_RAPORU_YAZILMAMIS: "Raporu yaz",
  VELI_RAPORU_BEKLIYOR: "Raporu hazırla",
  NET_DUSEN: "Analize bak",
};

/**
 * ================================================================
 * AYNI TÜR İŞLER GRUPLANIYOR — UI/UX Dalga 2 (M3)
 * ================================================================
 * Ölçüldü: bir uzmanda 42 iş, aynı cümle ("4 gündür veri girmedi") altı kez
 * üst üste. Düz liste, "bugün asıl ne yapacağım" sorusunu cevaplamayı
 * zorlaştırıyordu: ikinci türün ilk satırı ekranın dışında kalıyordu.
 *
 * Şimdi iş TÜRÜNE göre gruplu: her grupta başlık + sayı (+ kritik sayısı) ve
 * ilk ${GRUP_ILK_ADET} iş; fazlası "N işi daha göster" ile açılıyor. Hiçbir iş
 * kaybolmuyor — gizlenen satırın SAYISI düğmede yazıyor.
 *
 * SIRALAMA KORUNUYOR: gruplar listede ilk göründükleri sırayla (sunucu
 * önceliğe göre sıralı gönderiyor), grup içi sıra da aynı. Yani en kritik iş
 * hangi gruptaysa o grup üstte; "kritik olan neydi" bilgisi kaybolmuyor.
 */
const GRUP_ILK_ADET = 3;

/**
 * Koç anasayfası: üstte UNİKOÇ düzeninde özet (`KocOzeti`: duyuru bandı, altı renkli kart,
 * bildirim/eksik rapor/oturum şeridi), altında iş listesi. Şerit, iş listesiyle AYNI kitleye (uzman + analiz izni) gösterilir:
 * iş listesinin çekildiği kişiye özet de çekilir, ikisi ayrı kural taşımaz.
 */
export function IsListesi(props: IsListesiProps) {
  const ozetGoster = props.uzmanMi && "analiz.ogrenci.goruntule" in props.izinler;
  return (
    <>
      {ozetGoster && (
        <KocOzeti
          izinler={props.izinler}
          uzmanMi={props.uzmanMi}
          onOgrenciSec={props.onOgrenciSec}
        />
      )}
      {/* Özetteki "Görüntüle" düğmesi buraya kaydırıyor (UNİKOÇ'taki eksik kayıt listesi). */}
      {/* Sarmalayıcı yalnız liste çizilebilecekken: yetkisizde bileşen hiç yer kaplamamalı. */}
      {ozetGoster ? (
        <div id="yapilacaklar" className="scroll-mt-4">
          <IsListesiIc {...props} />
        </div>
      ) : (
        <IsListesiIc {...props} />
      )}
    </>
  );
}

function IsListesiIc({ onOgrenciSec, izinler, uzmanMi }: IsListesiProps) {
  const [tur, setTur] = useState<IsKalemi["tur"] | null>(null);
  /** Tamamı açılan gruplar (UI/UX Dalga 2, M3). Varsayılan: her grupta ilk ${GRUP_ILK_ADET} iş. */
  const [genisleyen, setGenisleyen] = useState<ReadonlySet<IsKalemi["tur"]>>(new Set());
  /*
    ================================================================
    YETKI YOKSA HIC SORULMUYOR — Emre'nin karari, 06.09.2026
    ================================================================
    Uc `analiz.ogrenci.goruntule` istiyor. Teknik yoneticide o izin
    YOK ve istek guard'a takiliyor: ekran "Is listesi alinamadi — bu
    islem icin yetkiniz yok" karti gosteriyordu ve her acilis bir
    "erisim reddedildi" olayi uretiyordu.

    Asagidaki `uzmanDegil` dali bunu YAKALAMIYOR: o dal servisin
    dondugu `UZMAN_DEGIL` kodunu ariyor, ama 403 guard'dan geliyor ve
    servise hic ulasilmiyor. Yani "uzman degilse sessizce cekil"
    karari dogruydu, uygulanma yeri eksikti.

    ⚠️ Bu bir GUVENLIK kontrolu DEGIL, gurultu onleme. Yetkiyi sunucu
    uyguluyor.
  */
  /*
    ================================================================
    IZIN YETMIYOR, UZMANLIK DA SORULUYOR — 12.09.2026
    ================================================================
    Burasi yalnizca izne bakiyordu ve yukaridaki gerekce Teknik
    Yonetici icin dogru calisiyordu (onda izin YOK, sorgu hic
    atilmiyor). Ama Operasyon Admini'nde izin VAR: sorgu atiliyor,
    uc `UZMAN_DEGIL` donduruyor ve her acilis bir 403 uretiyordu.
    Kullaniciya gorunmuyordu — asagidaki `uzmanDegil` dali kutuyu
    sessizce gizliyor — ama log ve konsol her girişte kirleniyordu.

    Iki AYRI soru tek alana bindirilmisti: "bu veriyi gorebilir mi"
    (izin) ve "bu kisi uzman mi" (kayit). Ikincisi artik sunucudan
    geliyor ve kaynagi `analiz.isListesi()` ile AYNI — koc kaydinin
    varligi.

    Asagidaki `uzmanDegil` dali KALDIRILMADI: `Coach` kaydi ile ucun
    kendi kontrolu arasinda yaris payi kalirsa (kayit istek sirasinda
    arsivlenirse) ekran yine dogru davranmali.
  */
  const izinliMi = "analiz.ogrenci.goruntule" in izinler && uzmanMi;

  const { data, isPending, isError, error, refetch, isFetching } = useQuery<IsKalemi[]>({
    queryKey: ["is-listesi"],
    queryFn: isListesiGetir,
    enabled: izinliMi,
    /**
     * ================================================================
     * 403 TEKRAR DENEMEKLE GECMEZ
     * ================================================================
     * Bu uc uzman OLMAYAN bir hesaba her zaman `UZMAN_DEGIL` donuyor —
     * yani deterministik bir red. Varsayilan davranis (uc deneme, artan
     * bekleme) o redde ~8 SANIYE iskelet gosteriyordu ve panelin ACILIS
     * ekrani, firma sahibi icin sekiz saniye gri cubuklarla basliyordu.
     *
     * Yetki iki deneme arasinda degismiyor; beklemek yalnizca kullanicinin
     * zamanini harciyor.
     */
    retry: false,
  });

  /*
    IZIN KONTROLU `isPending`TEN ONCE. `enabled: false` iken sorgu
    sonsuza kadar "bekliyor" halinde kalir ve iskelet hic kaybolmazdi —
    yani hata karti yerine sonsuz gri cubuklar gorunurdu.
  */
  if (!izinliMi) return null;

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    const uzmanDegil = error instanceof ApiHatasi && error.kod === "UZMAN_DEGIL";
    /**
     * ================================================================
     * UZMAN DEGILSE HIC CIZILMIYOR
     * ================================================================
     * Onceden burada "Yonetici gorunumu ayri yazilacak" diyen bir bos
     * durum vardi ve panelin ACILIS ekrani, firma sahibi icin bir
     * yer tutucuydu. Artik yonetici kendi ekranini goruyor
     * (`YoneticiBugun`); bu bilesen o kisi icin sessizce cekiliyor.
     *
     * `null` donmek dogru olan: "is listesi yok" demek DEGIL, "bu
     * bolum sana ait degil" demek. Ekranda yer kaplamamali.
     */
    if (uzmanDegil) return null;
    return (
      <BosDurum
        ton="hata"
        baslik="İş listesi alınamadı"
        aciklama={error instanceof Error ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.length === 0) {
    return (
      <BosDurum
        ikon="tamam"
        baslik="Bekleyen iş yok"
        aciklama="Bütün öğrencilerin bu haftaki programı hazır, görüşme raporları yazılmış ve veri girişi güncel."
        eylem={
          <Buton gorunum="sessiz" onClick={() => void refetch()} yukleniyor={isFetching}>
            Yenile
          </Buton>
        }
      />
    );
  }

  // Sayim SUZGECTEN ONCE: cipte "kac tane var" yaziyor ve o sayi
  // suzgece gore degisseydi cipin kendisi anlamsizlasirdi.
  const turSayilari = new Map<IsKalemi["tur"], number>();
  for (const k of data) turSayilari.set(k.tur, (turSayilari.get(k.tur) ?? 0) + 1);
  const gorunen = tur === null ? data : data.filter((k) => k.tur === tur);
  const gruplar = [...new Set(gorunen.map((k) => k.tur))].map((t) => {
    const kalemler = gorunen.filter((k) => k.tur === t);
    const acik = genisleyen.has(t);
    return {
      tur: t,
      kalemler,
      gorunenKalemler: acik ? kalemler : kalemler.slice(0, GRUP_ILK_ADET),
      acik,
      kritik: kalemler.filter((k) => k.oncelik === "KRITIK").length,
    };
  });
  const genislemeyiDegistir = (t: IsKalemi["tur"]): void =>
    setGenisleyen((onceki) => {
      const yeni = new Set(onceki);
      if (yeni.has(t)) yeni.delete(t);
      else yeni.add(t);
      return yeni;
    });

  return (
    <Kart gorunum="unk" olarak="div" className="flex flex-col gap-4">
      {/*
        BASLIK SATIRI CIZIMDEN: solda "Yapilacaklar" (15px/600), sagda
        siralama olcutu (13px). Sayim atilmadi, sag tarafa tasindi —
        cizim onu gostermiyor ama var olan bir bilgiyi silmek de
        giydirme degil kayip olurdu.
      */}
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-metin-koyu flex flex-wrap items-center gap-x-2 text-xl font-bold">
          <Ikon ad="gorevler" className="text-mor-500 size-6" />
          Yapılacaklar
          <span className="text-notr-600 ml-2 text-[13px] font-normal">
            {data.length} iş · önceliğe göre sıralı
          </span>
          {data.some((k) => k.oncelik === "KRITIK") && (
            <span className="text-hata-700 ml-2 text-[13px] font-medium">
              {data.filter((k) => k.oncelik === "KRITIK").length} kritik
            </span>
          )}
        </p>
        <Buton gorunum="kesikli" onClick={() => void refetch()} yukleniyor={isFetching}>
          Yenile
        </Buton>
      </div>

      {/*
        Cipler yalnizca LISTEDE VAR OLAN turler icin ciziliyor: hicbir
        isi olmayan bir tur icin cip gostermek, tiklayinca bos liste
        veren bir dugme demek.
      */}
      {turSayilari.size > 1 && (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Türe göre süz">
          <button
            type="button"
            onClick={() => setTur(null)}
            aria-pressed={tur === null}
            className={cn(
              "rounded-rozet min-h-dokunma px-3 text-[13px] font-medium ring-1 ring-inset",
              "ease-cikis transition-colors duration-(--sure-anlik)",
              tur === null
                ? "text-notr-0 bg-(image:--gradyan-unk-birincil) ring-transparent"
                : "bg-notr-0 text-metin-koyu ring-mor-kenar hover:bg-mor-50",
            )}
          >
            Tümü ({data.length})
          </button>
          {[...turSayilari.entries()].map(([t, n]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTur(tur === t ? null : t)}
              aria-pressed={tur === t}
              className={cn(
                "rounded-rozet min-h-dokunma px-3 text-[13px] font-medium ring-1 ring-inset",
                "ease-cikis transition-colors duration-(--sure-anlik)",
                tur === t
                  ? "text-notr-0 bg-(image:--gradyan-unk-birincil) ring-transparent"
                  : "bg-notr-0 text-metin-koyu ring-mor-kenar hover:bg-mor-50",
              )}
            >
              {TUR_ETIKETI[t]} ({n})
            </button>
          ))}
        </div>
      )}

      <AnketOzeti />

      {gruplar.map((grup) => (
        <section key={grup.tur} aria-label={TUR_ETIKETI[grup.tur]} className="flex flex-col gap-2">
          {/*
            TEK GRUP VARSA BAŞLIK YOK: "Yapılacaklar · 13 iş" satırı zaten aynı şeyi
            söylüyor ve başlık ilk iş kartını ~28 px aşağı itiyordu (Playwright, 390 px).
            Bölüm adı yine `aria-label`da: ekran okuyucu grubu adıyla duyuyor.
          */}
          {gruplar.length > 1 && (
            <h3 className="text-notr-700 text-[13px] font-semibold">
              {TUR_ETIKETI[grup.tur]}
              <span className="text-notr-600 ml-1.5 font-normal">· {grup.kalemler.length} iş</span>
              {grup.kritik > 0 && (
                <span className="text-hata-700 ml-1.5 font-medium">{grup.kritik} kritik</span>
              )}
            </h3>
          )}
          <ul className="flex flex-col gap-2">
            {grup.gorunenKalemler.map((kalem) => {
              const g = ONCELIK_GORUNUM[kalem.oncelik];
              return (
                <li key={`${kalem.tur}-${kalem.ogrenciId}`}>
                  {/*
                SATIR `SatirDugmesi` DEGIL: o genel bir kart satiri ve
                onceligi tasiyamiyor (kenar rengi ve sol serit). Cizimin
                kalibi burada aciliyor ama sozlesme ayni — `<button>`,
                44px dokunma hedefi, odak halkasi.
              */}
                  <button
                    type="button"
                    onClick={() => onOgrenciSec(kalem.ogrenciId, kalem.ogrenciAdi, kalem.tur)}
                    className={cn(
                      "min-h-dokunma bg-notr-0 flex w-full items-center gap-3.5 rounded-[1rem] border",
                      "px-4 py-3.5 text-left shadow-(--golge-unk-kart)",
                      "ease-cikis transition-[background-color,box-shadow] duration-(--sure-anlik)",
                      "hover:shadow-yukselen",
                      "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                      g.kenar,
                    )}
                  >
                    {/*
                  SOL SERIT: 3px, satir boyunca uzuyor (`self-stretch`).
                  `aria-hidden` — onceligi zaten cip METINLE soyluyor.
                */}
                    <span
                      aria-hidden="true"
                      className={cn("rounded-rozet w-[3px] shrink-0 self-stretch", g.serit)}
                    />

                    {/*
                  SIRA CIZIMDEN: ustte YAPILACAK IS, altta ogrenci.
                  Kodda tersiydi. Uzman listeye "ne yapmam gerekiyor"
                  diye bakiyor; ogrenci adi o isin kime ait oldugunu
                  soyleyen ikincil bilgi.
                */}
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      {/*
                    ================================================================
                    TELEFONDA IKI SATIR, MASAUSTUNDE TEK SATIR — 07.09.2026
                    ================================================================
                    390 px'te olculdu: "Bu haftanin programi hazirlanmamis"
                    248 px istiyor, 109 px aliyor — yani basligin YARIDAN
                    fazlasi kesiliyordu ve ekranda "Bu haftanin pro..."
                    kaliyordu. "3 gundur veri girmedi" 148 -> 128 px.

                    Kesilen sey listenin ASIL bilgisi: uzman bu listeye
                    "ne yapmam gerekiyor" diye bakiyor. Ogrenci adi
                    kesilse ikincil bilgi kaybolurdu; BASLIK kesilince
                    satirin varlik sebebi kayboluyor.

                    Masaustunde yer var ve tek satirlik ritim dogru;
                    dar ekranda ikinci satira izin veriliyor. `truncate`
                    yerine `line-clamp-2`: sinirsiz buyumesin, satir
                    yuksekligi ongorulebilir kalsin.
                  */}
                      <span className="text-notr-900 line-clamp-2 text-[15px] font-medium md:truncate">
                        {kalem.baslik}
                      </span>
                      <span className="text-notr-600 truncate text-[13px]">{kalem.ogrenciAdi}</span>
                    </span>

                    {/* Cip `radius-alan` — cizimden; yuvarlak hap DEGIL. */}
                    <span
                      className={cn(
                        "rounded-alan inline-flex shrink-0 px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                        g.sinif,
                      )}
                    >
                      {g.etiket}
                    </span>

                    {/*
                  Eylem etiketi `aria-hidden`, yani ekran okuyucuya zaten
                  hicbir sey soylemiyor — satirin tamami tiklanabilir ve
                  ne yapacagi basliktan belli. Dar ekranda bu susleme
                  basliktan yer caliyordu; orada yalnizca ok kaliyor.
                */}
                    <span
                      className="text-mor-700 shrink-0 text-sm font-semibold"
                      aria-hidden="true"
                    >
                      <span className="hidden sm:inline">{TUR_EYLEMI[kalem.tur]} </span>→
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {grup.kalemler.length > GRUP_ILK_ADET && (
            <div>
              <Buton
                gorunum="sessiz"
                onClick={() => genislemeyiDegistir(grup.tur)}
                aria-expanded={grup.acik}
              >
                {grup.acik
                  ? "Daha az göster"
                  : `${grup.kalemler.length - GRUP_ILK_ADET} işi daha göster`}
              </Buton>
            </div>
          )}
        </section>
      ))}
    </Kart>
  );
}

/**
 * UZMANIN KENDİ ANKET ÖZETİ — F-SRV-07
 *
 * `GET /anketler/ozetim` yazılıydı, hiçbir ekran çağırmıyordu:
 * velilerin doldurduğu memnuniyet anketi uzmanın kendisine
 * kapalıydı. Şartname "anket sonuçları koça açıktır" diyor.
 *
 * ================================================================
 * ANONİM — HANGİ VELİNİN YAZDIĞI YOK
 * ================================================================
 * Şartnamenin devamı: "ancak tek tek hangi velinin yazdığı
 * gösterilmez." Sunucu zaten kimlik döndürmüyor; ekran da
 * yorumları isimsiz gösteriyor ve bunu YAZIYOR — okuyan kişi
 * "acaba kim yazdı" diye aramasın.
 *
 * ================================================================
 * `null` = HİÇ ANKET GÖNDERİLMEDİ
 * ================================================================
 * "%0 yanıt oranı" yazmak, gönderilmiş ama cevaplanmamış bir anket
 * varmış gibi gösterirdi. Gönderilmemiş bir anketin yanıt oranı
 * sıfır değil, YOKTUR.
 */
function AnketOzeti() {
  const [acik, setAcik] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ["anket-ozetim"],
    queryFn: anketOzetim,
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          Veli memnuniyet özetim
        </Buton>
      </div>
    );
  }

  // Yetkisi olmayana hicbir sey gosterilmiyor: "ozet alinamadi"
  // demek, olmayan bir arizayi haber vermek olurdu.
  if (isError) return null;

  return (
    <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-notr-900 text-sm font-semibold">Veli memnuniyet özetim</h3>
        <Buton gorunum="sessiz" onClick={() => setAcik(false)}>
          Kapat
        </Buton>
      </div>

      {isPending ? (
        <Iskelet satir={3} />
      ) : data.gonderilen === 0 ? (
        <p className="text-notr-600 text-sm">
          Henüz anket gönderilmemiş. Bu, memnuniyetin ölçülmediği anlamına gelir — düşük olduğu
          değil.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ["Gönderilen", String(data.gonderilen)],
                ["Yanıtlanan", String(data.yanitlanan)],
                [
                  "Yanıt oranı",
                  data.yanitOrani === null ? "—" : `%${Math.round(data.yanitOrani * 100)}`,
                ],
                ["Ortalama", data.ortalama === null ? "—" : data.ortalama.toFixed(1)],
              ] as const
            ).map(([etiket, deger]) => (
              <div key={etiket} className="rounded-alan bg-notr-50 p-3">
                <dt className="text-notr-600 text-xs">{etiket}</dt>
                <dd className="text-notr-900 mt-0.5 text-lg font-semibold tabular-nums">{deger}</dd>
              </div>
            ))}
          </dl>

          {data.npsSkoru !== null && (
            <p className="text-notr-700 text-sm">
              NPS skoru: <strong className="tabular-nums">{data.npsSkoru}</strong>
            </p>
          )}

          {data.yorumlar.length > 0 && (
            <div>
              <p className="text-notr-800 text-sm font-medium">
                Yorumlar{" "}
                <span className="text-notr-600 font-normal">
                  — isimsiz, hangi velinin yazdığı gösterilmez
                </span>
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {data.yorumlar.map((yorum, i) => (
                  <li key={i} className="rounded-alan bg-notr-50 text-notr-700 p-2 text-sm">
                    {yorum}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
